import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Search, Eye, Pencil, Trash2, Copy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

// Best human identifier for a raw record, whatever collection it came from.
const recordTitle = (r) => r.transaction_id || r.token_no || r.username || r.name || r.key || r.record_id || r._id;
const recordSub = (r) => [r.devotee_name, r.full_name, r.ticket?.ticket_no && `Ticket ${r.ticket.ticket_no}`, r.mobile || r.phone, r.action]
  .filter(Boolean).join(" · ");

export default function DataManager() {
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState("");
  const [coll, setColl] = useState("");
  const [results, setResults] = useState(null);
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editText, setEditText] = useState("");
  const [del, setDel] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const loadCollections = () => api.get("/admin/data/collections").then((r) => setCollections(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === "SUPER_ADMIN") loadCollections(); }, [user]);

  if (user?.role !== "SUPER_ADMIN") return <Navigate to="/" replace />;

  const label = (name) => collections.find((c) => c.name === name)?.label || name;
  const readOnly = (name) => collections.find((c) => c.name === name)?.read_only;

  const search = (collection = coll) => {
    if (!q.trim() && !collection) { toast.error("Enter a Token No, Transaction ID, ticket no, username, mobile or name"); return; }
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (collection) p.set("collection", collection);
    setBusy(true);
    api.get(`/admin/data/search?${p}`).then((r) => setResults(r.data.results)).catch((e) => toast.error(fmtErr(e))).finally(() => setBusy(false));
  };

  const openEdit = (item) => { setEdit(item); setEditText(JSON.stringify(item.record, null, 2)); setReason(""); };

  const saveEdit = async () => {
    let record;
    try { record = JSON.parse(editText); } catch (e) { toast.error(`Invalid JSON: ${e.message}`); return; }
    if (!record || typeof record !== "object" || Array.isArray(record)) { toast.error("Record must be a JSON object"); return; }
    if (!reason.trim()) { toast.error("Reason for the change is required"); return; }
    try {
      const r = await api.put(`/admin/data/${edit.collection}/${encodeURIComponent(edit.record._id)}`, { record, reason });
      setResults((rs) => rs.map((x) => (x === edit ? { ...x, record: r.data } : x)));
      toast.success("Record updated (audited)");
      setEdit(null);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doDelete = async () => {
    if (!reason.trim()) { toast.error("Reason for deletion is required"); return; }
    try {
      await api.delete(`/admin/data/${del.collection}/${encodeURIComponent(del.record._id)}`, { data: { reason } });
      setResults((rs) => rs.filter((x) => x !== del));
      toast.success("Record deleted (audited)");
      setDel(null);
      loadCollections();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const copy = (obj) => navigator.clipboard?.writeText(JSON.stringify(obj, null, 2)).then(() => toast.success("Copied"));

  return (
    <div data-testid="data-manager-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Data Manager</h1>
      <p className="text-sm text-slate-500 mb-4">Super Admin only. Records are shown exactly as stored in the database. Every edit and delete needs a reason and is written to the audit log.</p>

      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-3"><Label className="text-xs">Search</Label>
            <Input data-testid="dm-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Token No / Transaction ID / Ticket No / username / mobile / name / record id" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Collection</Label>
            <Select value={coll || "ALL"} onValueChange={(v) => setColl(v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="dm-collection"><SelectValue placeholder="All collections" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All collections</SelectItem>
                {collections.map((c) => <SelectItem key={c.name} value={c.name}>{c.label} ({c.count})</SelectItem>)}</SelectContent>
            </Select></div>
          <Button data-testid="dm-search-btn" onClick={() => search()} disabled={busy} className="bg-maroon-700 hover:bg-maroon-800 text-white">
            <Search className="w-4 h-4 mr-1" /> {busy ? "Searching…" : "Search"}
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="dm-collections">
        {collections.map((c) => (
          <button key={c.name} type="button" onClick={() => { setColl(c.name); search(c.name); }}
            className={`text-xs px-3 py-1 rounded-full border ${coll === c.name ? "bg-maroon-800 text-white border-maroon-800" : "border-[#E5D9C5] text-maroon-900 hover:bg-[#FBF7EF]"}`}>
            {c.label} <span className="opacity-70">{c.count}</span>
          </button>
        ))}
      </div>

      {results && (
        <Card className="shadow-sm">
          <CardContent className="overflow-x-auto p-4">
            <p className="text-xs text-slate-500 mb-2" data-testid="dm-count">{results.length} record(s){results.length >= 50 ? " — showing the latest 50 per collection; refine the search to narrow down" : ""}</p>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
                <th className="py-2 pr-4">Collection</th><th className="py-2 pr-4">Record</th><th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Created</th><th className="py-2">Actions</th>
              </tr></thead>
              <tbody>
                {results.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No matching records</td></tr>}
                {results.map((item, i) => (
                  <tr key={`${item.collection}-${item.record._id}`} className="border-b border-[#F1EADB]" data-testid={`dm-row-${i}`}>
                    <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap">{label(item.collection)}</td>
                    <td className="py-2 pr-4"><p className="font-semibold text-maroon-900">{recordTitle(item.record)}</p>
                      <p className="text-xs text-slate-500">{recordSub(item.record)}</p></td>
                    <td className="py-2 pr-4 text-xs">{item.record.status || "—"}</td>
                    <td className="py-2 pr-4 text-xs whitespace-nowrap">{item.record.created_at ? new Date(item.record.created_at).toLocaleString("en-IN") : "—"}</td>
                    <td className="py-2 whitespace-nowrap">
                      <Button size="sm" variant="ghost" title="View" onClick={() => setView(item)} data-testid={`dm-view-${i}`}><Eye className="w-4 h-4" /></Button>
                      {!readOnly(item.collection) && <>
                        <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(item)} data-testid={`dm-edit-${i}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" title="Delete" className="text-red-600" onClick={() => { setDel(item); setReason(""); }} data-testid={`dm-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">{view && `${label(view.collection)} · ${recordTitle(view.record)}`}</DialogTitle></DialogHeader>
          {view && <>
            <pre className="text-xs bg-slate-950 text-slate-100 rounded-md p-4 max-h-[65vh] overflow-auto whitespace-pre-wrap break-all" data-testid="dm-view-json">{JSON.stringify(view.record, null, 2)}</pre>
            <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => copy(view.record)}><Copy className="w-4 h-4 mr-1" /> Copy JSON</Button></div>
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">{edit && `Edit ${label(edit.collection)} · ${recordTitle(edit.record)}`}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <p className="text-xs text-amber-700 flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> The record is saved exactly as written. "$file" entries point to stored photos/letters — keep them unchanged.</p>
            <Textarea className="font-mono text-xs min-h-[50vh]" value={editText} onChange={(e) => setEditText(e.target.value)} data-testid="dm-edit-json" />
            <div><Label className="text-xs">Reason for change *</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dm-edit-reason" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
              <Button onClick={saveEdit} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="dm-edit-save">Save changes</Button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-red-700">Delete record permanently?</DialogTitle></DialogHeader>
          {del && <div className="space-y-3">
            <p className="text-sm">{label(del.collection)} · <b>{recordTitle(del.record)}</b> will be removed from the database. This cannot be undone.</p>
            <div><Label className="text-xs">Reason for deletion *</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dm-delete-reason" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDel(null)}>Cancel</Button>
              <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="dm-delete-confirm">Delete</Button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
